import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, Calendar, User as UserIcon, Twitter, Facebook, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

function TOC({ items, activeId }) {
  if (!items || items.length === 0) return null;
  return (
    <nav aria-label="Table of contents" data-testid="article-toc" className="text-sm">
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">On this page</div>
      <ul className="space-y-1.5">
        {items.map((h) => (
          <li key={h.id} className={h.level === 3 ? "ml-3" : ""}>
            <a href={`#${h.id}`} className={`block text-sm transition-colors ${activeId === h.id ? "text-blue-700 font-medium" : "text-slate-600 hover:text-slate-900"}`}>{h.text}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function ResearchArticle() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeHeading, setActiveHeading] = useState("");

  useEffect(() => {
    setA(null); setNotFound(false);
    api.get(`/research/${slug}`).then((r) => {
      setA(r.data);
      window.scrollTo(0, 0);
      // Fire view increment (best effort)
      api.post(`/research/${slug}/view`).catch(() => {});
      // Inject schemas
      const existing = document.querySelectorAll("script[data-research-schema]");
      existing.forEach((el) => el.remove());
      (r.data.schemas || []).forEach((schema) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-research-schema", "1");
        s.textContent = JSON.stringify(schema);
        document.head.appendChild(s);
      });
      // Set title + meta
      document.title = `${r.data.seo_title || r.data.title} · Peps4Pets Research`;
      const meta = document.querySelector('meta[name="description"]') || (() => { const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m; })();
      meta.content = r.data.meta_description || r.data.excerpt || "";
    }).catch(() => setNotFound(true));
    return () => {
      document.querySelectorAll("script[data-research-schema]").forEach((el) => el.remove());
    };
  }, [slug]);

  // Scrollspy for TOC
  useEffect(() => {
    if (!a?.toc?.length) return;
    const onScroll = () => {
      let current = "";
      for (const h of a.toc) {
        const el = document.getElementById(h.id);
        if (el && el.getBoundingClientRect().top < 140) current = h.id;
      }
      setActiveHeading(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [a]);

  const share = (channel) => {
    const url = window.location.href;
    if (channel === "copy") { navigator.clipboard.writeText(url).then(() => toast.success("Link copied")); return; }
    if (channel === "twitter") window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(a.title)}`, "_blank");
    if (channel === "facebook") window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank");
  };

  const references = a?.references || [];
  const apaSorted = useMemo(() => (references || []).map((r, i) => ({ ...r, n: i + 1 })), [references]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto py-24 text-center">
      <h1 className="font-display text-3xl font-semibold">Article not found</h1>
      <Link to="/research" className="mt-4 inline-flex items-center gap-1 text-blue-700 hover:text-blue-800"><ArrowLeft className="w-4 h-4"/> Back to Research Hub</Link>
    </div>
  );
  if (!a) return <div className="max-w-6xl mx-auto py-24 text-sm text-slate-400 font-mono uppercase tracking-widest text-center">Loading…</div>;

  return (
    <article data-testid="article-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/research" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6"><ArrowLeft className="w-4 h-4"/> Research Hub</Link>

      <header className="mb-8 max-w-3xl">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {a.category && <Badge className="rounded-md font-mono text-[10px] uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200">{a.category.name}</Badge>}
          {(a.tags || []).map((t) => <Link key={t.slug} to={`/research/tag/${t.slug}`} className="text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900">#{t.name}</Link>)}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.1]">{a.title}</h1>
        {a.excerpt && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{a.excerpt}</p>}
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 font-mono">
          {a.author && <span className="inline-flex items-center gap-1"><UserIcon className="w-3.5 h-3.5"/> {a.author.name}</span>}
          {a.published_at && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/> {new Date(a.published_at).toLocaleDateString()}</span>}
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> {a.reading_time || 1} min read</span>
          {a.difficulty && <span className="inline-flex items-center gap-1">Level · {a.difficulty}</span>}
        </div>
      </header>

      {a.featured_image && <img src={a.featured_image} alt="" className="w-full rounded-md border border-slate-200 mb-10 max-h-[420px] object-cover"/>}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <TOC items={a.toc} activeId={activeHeading}/>
            <div className="mt-8">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Share</div>
              <div className="flex gap-2">
                <button onClick={() => share("twitter")} data-testid="share-twitter" className="p-2 rounded-md border border-slate-200 hover:border-slate-400 text-slate-600"><Twitter className="w-4 h-4"/></button>
                <button onClick={() => share("facebook")} className="p-2 rounded-md border border-slate-200 hover:border-slate-400 text-slate-600"><Facebook className="w-4 h-4"/></button>
                <button onClick={() => share("copy")} data-testid="share-copy" className="p-2 rounded-md border border-slate-200 hover:border-slate-400 text-slate-600"><LinkIcon className="w-4 h-4"/></button>
              </div>
            </div>
          </div>
        </aside>

        <main>
          <div className="article-body max-w-3xl" dangerouslySetInnerHTML={{ __html: a.content_html || "" }}/>

          {apaSorted.length > 0 && (
            <section className="max-w-3xl mt-14 border-t border-slate-200 pt-8">
              <h2 className="font-display text-2xl font-semibold text-slate-900 mb-4">References</h2>
              <ol className="space-y-3 text-sm">
                {apaSorted.map((r) => (
                  <li key={r.n} className="text-slate-700 leading-relaxed">
                    <span className="text-slate-400 mr-1">[{r.n}]</span>
                    <span>{r.authors ? `${r.authors} ` : ""}{r.year ? `(${r.year}). ` : ""}{r.title ? `${r.title}. ` : ""}{r.journal ? <em>{r.journal}. </em> : null}
                      {r.doi && <a href={r.doi.startsWith("http") ? r.doi : `https://doi.org/${r.doi}`} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{r.doi}</a>}
                      {!r.doi && r.pubmed && <a href={r.pubmed} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">PubMed</a>}
                      {!r.doi && !r.pubmed && r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{r.url}</a>}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {a.related_products_full?.length > 0 && (
            <section className="max-w-3xl mt-14 border-t border-slate-200 pt-8">
              <h2 className="font-display text-2xl font-semibold text-slate-900 mb-4">Related products</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {a.related_products_full.map((p) => (
                  <Link key={p.slug} to={`/peptides/${p.slug}`} className="border border-slate-200 rounded-md p-4 hover:border-blue-400 bg-white">
                    <div className="font-display font-semibold text-slate-900 text-sm">{p.name}</div>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mt-1">{p.category}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {a.related_articles_full?.length > 0 && (
            <section className="max-w-3xl mt-14 border-t border-slate-200 pt-8">
              <h2 className="font-display text-2xl font-semibold text-slate-900 mb-4">Related research</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {a.related_articles_full.map((r) => (
                  <Link key={r.slug} to={`/research/${r.slug}`} className="border border-slate-200 rounded-md p-4 hover:border-blue-400 bg-white">
                    <div className="font-display font-semibold text-slate-900 text-sm">{r.title}</div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">{r.excerpt}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="lg:hidden mt-10 flex gap-2 justify-center">
            <button onClick={() => share("twitter")} className="p-2 rounded-md border border-slate-200 text-slate-600"><Twitter className="w-4 h-4"/></button>
            <button onClick={() => share("facebook")} className="p-2 rounded-md border border-slate-200 text-slate-600"><Facebook className="w-4 h-4"/></button>
            <button onClick={() => share("copy")} className="p-2 rounded-md border border-slate-200 text-slate-600"><LinkIcon className="w-4 h-4"/></button>
          </div>
        </main>
      </div>
    </article>
  );
}
